import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTagsField from 'js/components/CustomTagsField'
import IconField from 'js/components/EntityActions/components/IconField'
import CoverField, {
    Ref as CoverFieldRef,
} from 'js/components/Form/CoverField/CoverField'
import FormItem from 'js/components/Form/FormItem'
import { Container } from 'js/components/Grid'
import Spacer from 'js/components/Spacer/Spacer'
import TagCategoriesField from 'js/components/TagCategories/TagCategoriesField'

import GroupTypePicker from './GroupTypePicker/GroupTypePicker'

type TBD = any

type Site = TBD

type Entity = TBD

type CustomTag = TBD

type TagCategory = { name: string; values: any[] }

type Props = {
    site: Site
    entity: Entity
    tags: TagCategory[]
    setTags: React.Dispatch<React.SetStateAction<TagCategory[]>>
    customTags: Set<CustomTag>
    setCustomTags: React.Dispatch<React.SetStateAction<Set<CustomTag>>>
    isMembershipOnRequest?: boolean
    setIsMembershipOnRequest: (isMembershipOnRequest: boolean) => void
    isClosed?: boolean
    setIsClosed: (isClosed: boolean) => void
}

const GeneralTab = ({
    ref,
    site,
    entity,
    tags,
    setTags,
    customTags,
    setCustomTags,
    isMembershipOnRequest,
    setIsMembershipOnRequest,
    isClosed,
    setIsClosed,
}: Props & {
    ref?: React.RefObject<CoverFieldRef>
}) => {
    const { control } = useFormContext()

    const { t } = useTranslation()

    const messageHelper = (
        <>
            {t('entity-group.field-welcome-helper')}
            <br />
            <span style={{ display: 'inline-block', width: '100px' }}>
                [name]
            </span>
            {t('entity-group.field-welcome-helper-username')}
            <br />
            <span style={{ display: 'inline-block', width: '100px' }}>
                [group_name]
            </span>
            {t('entity-group.field-welcome-helper-groupname')}
            <br />
            <span style={{ display: 'inline-block', width: '100px' }}>
                [group_url]
            </span>
            {t('entity-group.field-welcome-helper-groupurl')}
        </>
    )

    const handleAddTags = (tag: CustomTag) => {
        setCustomTags((previousTags) => {
            const newTags = new Set<CustomTag>(previousTags)
            newTags.add(tag)
            return newTags
        })
    }

    const handleRemoveTags = (tag: CustomTag) => {
        setCustomTags((previousTags) => {
            const newTags = new Set<CustomTag>(previousTags)
            newTags.delete(tag)
            return newTags
        })
    }

    return (
        <>
            <Container size="small" style={{ paddingTop: '24px' }}>
                <Spacer>
                    <CoverField
                        ref={ref}
                        featured={entity && entity.featured}
                        size="landscape-small"
                        disableCaption={true}
                    />

                    <IconField
                        iconProps={{
                            objectFit: 'cover',
                            style: {
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                            },
                        }}
                    />

                    <FormItem
                        control={control}
                        type="rich"
                        name="richDescription"
                        title={t('entity-group.field-description')}
                        helper={t('entity-group.field-description-helper')}
                        options={{
                            textStyle: true,
                            textLink: true,
                            textQuote: true,
                            textList: true,
                            insertMedia: true,
                        }}
                        testid="field-content"
                    />

                    <div>
                        <FormItem
                            control={control}
                            type="rich"
                            name="introduction"
                            title={t('form.introduction')}
                            helper={t('entity-group.field-intro-helper')}
                            options={{
                                textFormat: true,
                                textStyle: true,
                                textLink: true,
                                textQuote: true,
                                textList: true,
                                insertMedia: true,
                            }}
                            testid="field-introduction"
                        />
                        <FormItem
                            control={control}
                            type="switch"
                            name="isIntroductionPublic"
                            label={t(
                                'entity-group.field-intro-visible-non-members',
                            )}
                            size="small"
                        />
                    </div>

                    <div>
                        <FormItem
                            control={control}
                            type="rich"
                            name="welcomeMessage"
                            title={t('form.welcome-message')}
                            helper={messageHelper}
                            options={{
                                textFormat: true,
                                textStyle: true,
                            }}
                            contentType="html"
                            testid="field-welcome-message"
                        />
                    </div>

                    <TagCategoriesField
                        tagCategories={site.tagCategories}
                        value={tags}
                        setTags={setTags}
                    />
                    {site.customTagsAllowed && (
                        <CustomTagsField
                            name="customTags"
                            value={customTags}
                            onAddTag={handleAddTags}
                            onRemoveTag={handleRemoveTags}
                        />
                    )}
                </Spacer>
            </Container>
            <Container size="large" style={{ marginTop: '24px' }}>
                <GroupTypePicker
                    isMembershipOnRequest={isMembershipOnRequest}
                    onChangeIsMembershipOnRequest={setIsMembershipOnRequest}
                    isClosed={isClosed}
                    onChangeIsClosed={setIsClosed}
                />
            </Container>
        </>
    )
}

GeneralTab.displayName = 'GeneralTab'

export default GeneralTab
